import React from 'react';
import { Button, Modal, ModalBody, ModalHeader, Table } from 'reactstrap';
import StandardTideCurves from './StandardTideCurves';
import { clearStorage, loadFromStorage, saveToStorage } from '../../utils/localStorageUtils';
import ReactGA from '../../utils/analytics';

class TideForm extends React.Component {
  constructor(props) {
    super(props);
    const storageKey = props.storageKey
      || `tideform_${props.tabGroup || ''}_${window.location.pathname}`;
    const saved = loadFromStorage(storageKey, {});
    this.state = {
      editingCell: null,
      tideModal: false,
      storageKey,
      studentAnswers: saved.studentAnswers || {},
      checked: saved.checked || false,
    };
    this.toggleModal = this.toggleModal.bind(this);
  }

  toggleModal() {
    this.setState(prev => ({ tideModal: !prev.tideModal }));
  }

  handleCellClick = (cellId) => {
    this.setState({ editingCell: cellId });
  }

  handleCellBlur = () => {
    this.setState({ editingCell: null });
  }

  setAnswer = (field, value) => {
    this.setState(prev => {
      const studentAnswers = { ...prev.studentAnswers, [field]: value };
      saveToStorage(prev.storageKey, { studentAnswers, checked: prev.checked });
      return { studentAnswers };
    });
  }

  checkAnswers = () => {
    ReactGA.event({
      category: 'Checkign Answers in  Tide Form Exercises',
      action: `Checking ${this.props.tideform_tab_title}`
    });

    this.setState(prev => {
      saveToStorage(prev.storageKey, { studentAnswers: prev.studentAnswers, checked: true });
      return { checked: true };
    });
  }

  resetCheck = () => {
    this.setState(prev => {
      saveToStorage(prev.storageKey, { studentAnswers: prev.studentAnswers, checked: false });
      return { checked: false };
    });
  }

  clearForm = () => {
    if (window.confirm('Are you sure you want to clear all your answers? This cannot be undone.')) {
      clearStorage(this.state.storageKey);
      this.setState({ studentAnswers: {}, checked: false });
    }
  }

  getStatus = (adminValue, field, tolerance = 0) => {
    const { studentAnswers } = this.state;
    const student = (studentAnswers[field] || '').trim().toLowerCase();
    const admin = String(adminValue || '').trim().toLowerCase();
    if (!student) return 'missing';
    if (tolerance > 0) {
      const studentNum = parseFloat(student);
      const adminNum = parseFloat(admin);
      if (!isNaN(studentNum) && !isNaN(adminNum)) {
        return Math.abs(studentNum - adminNum) <= tolerance ? 'correct' : 'incorrect';
      }
    }
    return student === admin ? 'correct' : 'incorrect';
  }

  renderEditableCell = (adminValue, field, tolerance = 0) => {
    const { editingCell, studentAnswers, checked } = this.state;
    const cellId = `cell-${field}`;
    const studentValue = studentAnswers[field] || '';

    let tdStyle = {};
    let titleAttr;
    if (checked) {
      const status = this.getStatus(adminValue, field, tolerance);
      if (status === 'correct') tdStyle = { backgroundColor: '#d1e7dd' };
      else if (status === 'incorrect') {
        tdStyle = { backgroundColor: '#f8d7da' };
        if (tolerance > 0) {
          const adminNum = parseFloat(adminValue);
          titleAttr = !isNaN(adminNum)
            ? `Expected: ${(adminNum - tolerance).toFixed(2)} – ${(adminNum + tolerance).toFixed(2)}`
            : `Expected: ${adminValue}`;
        } else {
          titleAttr = `Expected: ${adminValue}`;
        }
      } else {
        tdStyle = { backgroundColor: '#fff3cd' };
      }
    }

    if (editingCell === cellId) {
      return (
        <td style={tdStyle}>
          <input
            type="text"
            value={studentValue}
            autoFocus
            style={{ width: '100%', border: '1px solid #007cba', padding: '4px', fontSize: '14px' }}
            onChange={(e) => this.setAnswer(field, e.target.value)}
            onBlur={this.handleCellBlur}
            onKeyPress={(e) => { if (e.key === 'Enter') this.handleCellBlur(); }}
          />
        </td>
      );
    }

    return (
      <td
        onClick={() => this.handleCellClick(cellId)}
        style={{ cursor: 'pointer', ...tdStyle }}
        title={titleAttr}
      >
        {studentValue || <span style={{ color: '#ccc' }}>Click to edit</span>}
      </td>
    );
  }

  renderSelectCell = (adminValue, field, options) => {
    const { studentAnswers, checked } = this.state;
    const studentValue = studentAnswers[field] || '';

    let tdStyle = {};
    let titleAttr;
    if (checked) {
      const status = this.getStatus(adminValue, field);
      if (status === 'correct') tdStyle = { backgroundColor: '#d1e7dd' };
      else if (status === 'incorrect') {
        tdStyle = { backgroundColor: '#f8d7da' };
        titleAttr = `Expected: ${adminValue}`;
      } else {
        tdStyle = { backgroundColor: '#fff3cd' };
      }
    }

    return (
      <td style={{ padding: '2px 4px', ...tdStyle }} title={titleAttr}>
        <select
          value={studentValue}
          onChange={(e) => this.setAnswer(field, e.target.value)}
          style={{ width: '100%', border: 'none', padding: '2px 2px', fontSize: '14px', backgroundColor: 'transparent', lineHeight: '1.2' }}
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </td>
    );
  }

  renderLegend = () => {
    if (!this.state.checked) return null;
    return (
      <div className="d-flex gap-3 mt-1 mb-3" style={{ fontSize: '0.8rem' }}>
        <span>
          <span style={{ display: 'inline-block', width: 18, height: 18, backgroundColor: '#d1e7dd', border: '1px solid #badbcc', marginRight: 5, verticalAlign: 'middle' }} />
          Correct
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 18, height: 18, backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', marginRight: 5, verticalAlign: 'middle' }} />
          Incorrect <small className="text-muted">(hover for expected)</small>
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 18, height: 18, backgroundColor: '#fff3cd', border: '1px solid #ffecb5', marginRight: 5, verticalAlign: 'middle' }} />
          Not answered
        </span>
      </div>
    );
  }

  render() {
    if (this.props.tideform_published === false) return null;

    const {
      tideform_description = '',
      tideform_show_secondary_port = true,
      tideform_show_tidal_range = true,
      tideform_show_vessel_requirements = true,
      tideform_show_required_tide = true,
      // Standard Port
      tideform_std_row1_tide = 'HW',
      tideform_std_row1_time = '',
      tideform_std_row1_height = '',
      tideform_std_row2_tide = 'LW',
      tideform_std_row2_time = '',
      tideform_std_row2_height = '',
      tideform_std_row3_tide = 'HW',
      tideform_std_row3_time = '',
      tideform_std_row3_height = '',
      tideform_std_row4_tide = 'LW',
      tideform_std_row4_time = '',
      tideform_std_row4_height = '',
      // Secondary Port - Table 1
      tideform_sec_row1_timediff = '',
      tideform_sec_row1_ratio = '',
      tideform_sec_row1_constant = '',
      tideform_sec_row2_timediff = '',
      // Secondary Port - Table 2
      tideform_sec_table2_row1_tide = 'HW',
      tideform_sec_table2_row1_time = '',
      tideform_sec_table2_row1_height = '',
      tideform_sec_table2_row2_tide = 'LW',
      tideform_sec_table2_row2_time = '',
      tideform_sec_table2_row2_height = '',
      tideform_sec_table2_row3_tide = 'HW',
      tideform_sec_table2_row3_time = '',
      tideform_sec_table2_row3_height = '',
      tideform_sec_table2_row4_tide = 'LW',
      tideform_sec_table2_row4_time = '',
      tideform_sec_table2_row4_height = '',
      // Tidal Range
      tideform_range_hw = '',
      tideform_range_lw = '',
      tideform_range_range = '',
      // Vessel Requirements
      tideform_vessel_ukc = '',
      tideform_vessel_draught = '',
      tideform_vessel_total = '',
      tideform_vessel_charteddepth = '',
      tideform_vessel_requiredheight = '',
      // Required Tide
      tideform_required_total = '',
      tideform_required_actuallw = '',
      tideform_required_tideheight = '',
      tideform_required_tidetime = '',
      // Tide Result (Section 7)
      tideform_show_tide_result = false,
      tideform_tide_result_tidetime = '',
      tideform_tide_result_timediff = '',
      tideform_tide_result_extra_height = '',
      tideform_tide_result_height = '',
      // Tide Time (Section 8)
      tideform_show_tide_time = false,
      tideform_tide_time_extrawater = '',
      tideform_tide_time_earliest = ''
    } = this.props;

    return (
      <div className="p-3">
        {tideform_description && (
          <div style={{ backgroundColor: '#f8f8f8', borderLeft: '5px solid #dddddd', borderRadius: '6px', padding: '16px 20px', marginBottom: '1.5rem', fontSize: '1.05rem', color: '#000000' }}>
            {tideform_description}
          </div>
        )}

        {/* Section 1: Standard Port */}
        <div className="mb-4">
          <div className="text-center mb-3">
            <h5>Tides for Standard Port</h5>
          </div>
          <Table bordered>
            <thead>
              <tr>
                <th>Tide (HW/LW)</th>
                <th>Time (HH:mm)</th>
                <th>Height</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {this.renderSelectCell(tideform_std_row1_tide, 'std_row1_tide', ['', 'HW', 'LW'])}
                {this.renderEditableCell(tideform_std_row1_time, 'std_row1_time')}
                {this.renderEditableCell(tideform_std_row1_height, 'std_row1_height')}
              </tr>
              <tr>
                {this.renderSelectCell(tideform_std_row2_tide, 'std_row2_tide', ['', 'HW', 'LW'])}
                {this.renderEditableCell(tideform_std_row2_time, 'std_row2_time')}
                {this.renderEditableCell(tideform_std_row2_height, 'std_row2_height')}
              </tr>
              <tr>
                {this.renderSelectCell(tideform_std_row3_tide, 'std_row3_tide', ['', 'HW', 'LW'])}
                {this.renderEditableCell(tideform_std_row3_time, 'std_row3_time')}
                {this.renderEditableCell(tideform_std_row3_height, 'std_row3_height')}
              </tr>
              <tr>
                {this.renderSelectCell(tideform_std_row4_tide, 'std_row4_tide', ['', 'HW', 'LW'])}
                {this.renderEditableCell(tideform_std_row4_time, 'std_row4_time')}
                {this.renderEditableCell(tideform_std_row4_height, 'std_row4_height')}
              </tr>
            </tbody>
          </Table>
          {this.renderLegend()}
        </div>

        {/* Section 2: Secondary Port */}
        {tideform_show_secondary_port && (
          <div className="mb-4">
            <div className="text-center mb-0">
              <h5>Tides for Secondary Port</h5>
            </div>
            <div className="text-center mb-3">
              <p>Semidiurnal/Diurnal Tidal plane values for secondary port</p>
            </div>
            <Table bordered className="mb-3">
              <tbody>
                <tr>
                  <td>HW</td>
                  {this.renderEditableCell(tideform_sec_row1_timediff, 'sec_row1_timediff')}
                </tr>
                <tr>
                  <td>LW</td>
                  {this.renderEditableCell(tideform_sec_row2_timediff, 'sec_row2_timediff')}
                </tr>
                <tr>
                  <td>Ratio</td>
                  {this.renderEditableCell(tideform_sec_row1_ratio, 'sec_row1_ratio')}
                </tr>
                <tr>
                  <td>Constant</td>
                  {this.renderEditableCell(tideform_sec_row1_constant, 'sec_row1_constant')}
                </tr>
              </tbody>
            </Table>
            <div className="text-center mb-3">
              <p>Calculated tide times and heights for secondary port using tidal plane values</p>
            </div>
            <Table bordered>
              <thead>
                <tr>
                  <th>Tide (HW/LW)</th>
                  <th>Time (HH:mm)</th>
                  <th>Height (2 decimals)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {this.renderSelectCell(tideform_sec_table2_row1_tide, 'sec_table2_row1_tide', ['', 'HW', 'LW'])}
                  {this.renderEditableCell(tideform_sec_table2_row1_time, 'sec_table2_row1_time')}
                  {this.renderEditableCell(tideform_sec_table2_row1_height, 'sec_table2_row1_height')}
                </tr>
                <tr>
                  {this.renderSelectCell(tideform_sec_table2_row2_tide, 'sec_table2_row2_tide', ['', 'HW', 'LW'])}
                  {this.renderEditableCell(tideform_sec_table2_row2_time, 'sec_table2_row2_time')}
                  {this.renderEditableCell(tideform_sec_table2_row2_height, 'sec_table2_row2_height')}
                </tr>
                <tr>
                  {this.renderSelectCell(tideform_sec_table2_row3_tide, 'sec_table2_row3_tide', ['', 'HW', 'LW'])}
                  {this.renderEditableCell(tideform_sec_table2_row3_time, 'sec_table2_row3_time')}
                  {this.renderEditableCell(tideform_sec_table2_row3_height, 'sec_table2_row3_height')}
                </tr>
                <tr>
                  {this.renderSelectCell(tideform_sec_table2_row4_tide, 'sec_table2_row4_tide', ['', 'HW', 'LW'])}
                  {this.renderEditableCell(tideform_sec_table2_row4_time, 'sec_table2_row4_time')}
                  {this.renderEditableCell(tideform_sec_table2_row4_height, 'sec_table2_row4_height')}
                </tr>
              </tbody>
            </Table>
            {this.renderLegend()}
          </div>
        )}

        {/* Section 3: Tidal Range */}
        {tideform_show_tidal_range && (
          <div className="mb-4">
            <div className="text-center mb-3">
              <h5>Tidal Range (Height)</h5>
            </div>
            <Table bordered>
              <tbody>
                <tr>
                  <td>HW</td>
                  {this.renderEditableCell(tideform_range_hw, 'range_hw')}
                </tr>
                <tr>
                  <td>LW</td>
                  {this.renderEditableCell(tideform_range_lw, 'range_lw')}
                </tr>
                <tr>
                  <td><strong>Range</strong></td>
                  {this.renderEditableCell(tideform_range_range, 'range_range')}
                </tr>
              </tbody>
            </Table>
            {this.renderLegend()}
          </div>
        )}

        {/* Section 4: Vessel Requirements */}
        {tideform_show_vessel_requirements && (
          <div className="mb-4">
            <div className="text-center mb-3">
              <h5>Your Vessel Requires</h5>
            </div>
            <Table bordered>
              <tbody>
                <tr>
                  <td>UKC Required</td>
                  {this.renderEditableCell(tideform_vessel_ukc, 'vessel_ukc')}
                </tr>
                <tr>
                  <td>Vessel Draft</td>
                  {this.renderEditableCell(tideform_vessel_draught, 'vessel_draught')}
                </tr>
                <tr>
                  <td><strong>Required Depth of Water (Draft + UKC)</strong></td>
                  {this.renderEditableCell(tideform_vessel_total, 'vessel_total')}
                </tr>
                <tr>
                  <td colSpan={2}>&nbsp;</td>
                </tr>
                <tr>
                  <td>Charted Depth</td>
                  {this.renderEditableCell(tideform_vessel_charteddepth, 'vessel_charteddepth')}
                </tr>
                <tr>
                  <td><strong>Required Height of Tide (Required Depth – Charted)</strong></td>
                  {this.renderEditableCell(tideform_vessel_requiredheight, 'vessel_requiredheight')}
                </tr>
              </tbody>
            </Table>
            {this.renderLegend()}

          </div>
        )}

        {/* Section 5: Low Water — commented out for now
        {tideform_show_low_water && (
          <div className="mb-4">
            <div className="text-center mb-3">
              <h5>You have at Low Water</h5>
            </div>
            <Table bordered>
              <tbody>
                <tr>
                  <td>Chart LAT</td>
                  {this.renderEditableCell(tideform_lowwater_chartlat, 'lowwater_chartlat')}
                </tr>
                <tr>
                  <td>LW</td>
                  {this.renderEditableCell(tideform_lowwater_lw, 'lowwater_lw')}
                </tr>
                <tr>
                  <td><strong>Actual Total</strong></td>
                  {this.renderEditableCell(tideform_lowwater_total, 'lowwater_total')}
                </tr>
              </tbody>
            </Table>
            {this.renderLegend()}
          </div>
        )}
        */}

        {/* Section 6: Required Tide Height */}
        {tideform_show_required_tide && (
          <div className="mb-4">
            <div className="text-center mb-3">
              <h5>Required Tide Height and when you can safely navigate over location</h5>
            </div>
            <Table bordered>
              <tbody>
                <tr>
                  <td>Total Required</td>
                  {this.renderEditableCell(tideform_required_total, 'required_total')}
                </tr>
                <tr>
                  <td>Actual at LW</td>
                  {this.renderEditableCell(tideform_required_actuallw, 'required_actuallw')}
                </tr>
                <tr>
                  <td><strong>Tide Height</strong></td>
                  {this.renderEditableCell(tideform_required_tideheight, 'required_tideheight')}
                </tr>
                <tr>
                  <td><strong>Tide Time</strong></td>
                  {this.renderEditableCell(tideform_required_tidetime, 'required_tidetime')}
                </tr>
              </tbody>
            </Table>
            {this.renderLegend()}
            <p className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
              Use this{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); this.toggleModal(); }}>
                Tidal Curve
              </a>
              {' '}to calculate Tide Height and/or Tide Time. The tidal bell provided is indicative and for learning purposes only.
            </p>
          </div>
        )}

        {/* Section 7: Tide Height */}
        {tideform_show_tide_result && (
          <div className="mb-4">
            <div className="text-center mb-3">
              <h5>Interpolated Tide Height</h5>
            </div>
            <Table bordered>
              <tbody>
                <tr>
                  <td>Estimated Arrival Time (HH:mm)</td>
                  {this.renderEditableCell(tideform_tide_result_tidetime, 'tide_result_tidetime')}
                </tr>
                <tr>
                  <td>Time difference to HW (+/-HH:mm)</td>
                  {this.renderEditableCell(tideform_tide_result_timediff, 'tide_result_timediff')}
                </tr>
                <tr>
                  <td><strong>Approximate extra Water over LW (Interpolate using Tidal Curve)</strong></td>
                  {this.renderEditableCell(tideform_tide_result_extra_height, 'tide_result_extra_height', 0.05)}
                </tr>
                <tr>
                  <td><strong>Total Tide Height</strong></td>
                  {this.renderEditableCell(tideform_tide_result_height, 'tide_result_height', 0.05)}
                </tr>
              </tbody>
            </Table>
            {this.renderLegend()}
            <p className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
              Use this{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); this.toggleModal(); }}>
                Tidal Curve
              </a>
              {' '}to calculate Tide Height and/or Tide Time. The tidal bell provided is indicative and for learning purposes only.
            </p>
          </div>
        )}

        {/* Section 8: Interpolated Tide Time */}
        {tideform_show_tide_time && (
          <div className="mb-4">
            <div className="text-center mb-3">
              <h5>Interpolated Tide Time (HH:mm)</h5>
            </div>
            <Table bordered>
              <tbody>
                <tr>
                  <td>Extra Water Height above LW (Required Tide Height - LW)</td>
                  {this.renderEditableCell(tideform_tide_time_extrawater, 'tide_time_extrawater')}
                </tr>
                <tr>
                  <td><strong>Approximate Entry/Exit time before/after HW (Hour interval +/- HW).</strong> Use Tidal Curve to calculate</td>
                  {this.renderEditableCell(tideform_tide_time_earliest, 'tide_time_earliest')}
                </tr>
              </tbody>
            </Table>
            {this.renderLegend()}
            <p className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
              Use this{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); this.toggleModal(); }}>
                Tidal Curve
              </a>
              {' '}to calculate Tide Height and/or Tide Time. The tidal bell provided is indicative and for learning purposes only.
            </p>
          </div>
        )}

        <div className="mt-3 mb-2 d-flex justify-content-center align-items-center gap-3">
          <Button color="success" style={{ color: 'white' }} onClick={this.checkAnswers}>
            Check Answers
          </Button>
          <Button color="link" onClick={this.resetCheck}>
            Reset Check
          </Button>
          <Button color="link" className="text-danger" onClick={this.clearForm}>
            Clear
          </Button>
        </div>

        <style>{'.tide-curves-modal .modal-dialog { max-width: min(1600px, 94vw) !important; }'}</style>
        <Modal isOpen={this.state.tideModal} toggle={this.toggleModal} wrapClassName="tide-curves-modal">
          <ModalHeader toggle={this.toggleModal}>Standard Tidal Curves</ModalHeader>
          <ModalBody>
            <StandardTideCurves />
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

export default TideForm;
